import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Icon } from "@iconify/react";
import { Link, useHistory, useParams } from "react-router-dom";
import axios from "axios";
import { format } from "date-fns";
import { useAuth } from "../core/AuthContext";
import { useSemester } from "../core/SemesterContext";
import { useDepartments } from "../core/DepartmentsContext";
import departmentService from "../core/services/DepartmentService";
import eventService from "../core/services/EventService";
import roleService from "../core/services/RoleService";
import semesterService from "../core/services/SemesterService";
import {
  canManageDepartmentPage,
  canViewDepartmentGmAttendance,
} from "../core/utils/permissionUtils";
import { isGeneralMeetingPresent } from "../core/utils/generalMeetingUtils";
import { URL } from "../core/config";
import NavHeader from "./NavHeader";
import LoadingSpinner from "./LoadingSpinner";
import Avatar from "./Avatar";
import CustomAlert from "./CustomAlert";
import CustomAlertWithButtons from "./CustomAlertWithButtons";
import EventListSection from "./events/EventListSection";
import OpenCallList from "./OpenCallList";
import useUsers from "./hooks/useUsers";
import useFetchUsersByIds, {
  collectEventUserRefs,
  offRosterUserIds,
} from "./hooks/useFetchUsersByIds";
import usePagination from "./hooks/usePagination";
import {
  buildEventsByOpenCallId,
  categorizeOpenCalls,
  countRecruitingOpenCalls,
  filterOpenCallsForDepartment,
} from "../core/utils/openCallUtils";
import {
  getDepartmentColor,
  getDepartmentHeadRoleName,
  getDepartmentIcon,
  getDepartmentId,
  getDepartmentName,
  departmentMatchesFilter,
} from "../core/constants/departments";
import "../styles/department-page.css";
import "../styles/departments.css";
import "../styles/userlist.css";
import "../styles/general.css";
import "../styles/main.css";
import "../styles/OpenCall.css";

const opencallURL = `${URL}/opencall`;
const EVENTS_PER_PAGE = 20;
const GM_ATTENDANCE_PER_PAGE = 20;
const GM_ATTENDANCE_GOOD_THRESHOLD = 51;
const HR_DEPARTMENT_NAME = "Human Resources";
const HR_GM_ATTENDANCE_SCOPE = {
  DEPARTMENT: "department",
  ORGANIZATION: "organization",
};

function buildDepartmentTabs(showGmAttendanceTab) {
  const tabs = [
    { id: "volunteers", label: "Volunteers" },
    { id: "events", label: "Events" },
  ];
  if (showGmAttendanceTab) {
    tabs.push({ id: "gm-attendance", label: "GM Attendance" });
  }
  tabs.push({ id: "announcements", label: "Open Calls" });
  return tabs;
}

function isUserGeneralMeetingParticipant(event, userId) {
  return isGeneralMeetingPresent(event, userId);
}

function computeGeneralMeetingAttendance(userId, generalMeetings) {
  const total = generalMeetings.length;
  if (total === 0) {
    return { attended: 0, total: 0, average: null };
  }
  const attended = generalMeetings.filter((event) =>
    isUserGeneralMeetingParticipant(event, userId),
  ).length;
  return {
    attended,
    total,
    average: ((attended / total) * 100).toFixed(2),
  };
}

function isToday(date) {
  const today = new Date();
  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth()
  );
}

function getMemberId(member) {
  if (!member) return null;
  return String(typeof member === "object" ? member._id : member);
}

function buildVolunteerChangesMessage(originalIds, currentIds, getUserName) {
  const originalSet = new Set(originalIds.map(String));
  const currentSet = new Set(currentIds.map(String));
  const addedIds = currentIds.filter((id) => !originalSet.has(String(id)));
  const removedIds = originalIds.filter((id) => !currentSet.has(String(id)));

  return (
    <>
      <p>You have unsaved volunteer changes for the current semester:</p>
      {addedIds.length > 0 && (
        <p>
          <strong>Added ({addedIds.length}):</strong>{" "}
          {addedIds.map(getUserName).join(", ")}
        </p>
      )}
      {removedIds.length > 0 && (
        <p>
          <strong>Removed ({removedIds.length}):</strong>{" "}
          {removedIds.map(getUserName).join(", ")}
        </p>
      )}
      <p>Save these changes before leaving edit mode</p>
    </>
  );
}

function resolveDepartmentMember(member, users) {
  const memberId = getMemberId(member);
  if (!memberId) return null;

  const fullUser = users.find((user) => String(user._id) === memberId);
  if (fullUser) return fullUser;

  return typeof member === "object" ? member : null;
}

function sortVolunteers(a, b) {
  const nameA = a.firstLastName?.trim() || a.fullName || a.name || "";
  const nameB = b.firstLastName?.trim() || b.fullName || b.name || "";
  return nameA.localeCompare(nameB);
}

function buildDepartmentMemberList(currentSemesterVolunteers, departmentHeadUser) {
  const membersById = new Map();
  const addMember = (user) => {
    if (user?._id) {
      membersById.set(String(user._id), user);
    }
  };

  currentSemesterVolunteers.forEach(addMember);
  addMember(departmentHeadUser);

  return Array.from(membersById.values()).sort(sortVolunteers);
}

function userHasAssignedRole(user) {
  return Array.isArray(user?.roles) && user.roles.length > 0;
}

function matchesUserSearch(user, query) {
  if (!query) return true;
  const normalizedQuery = query.trim().toLowerCase();
  const searchableFields = [
    user?.fullName,
    user?.firstLastName,
    user?.name,
    user?.nickname,
  ].filter(Boolean);

  return searchableFields.some((field) =>
    field.toLowerCase().includes(normalizedQuery),
  );
}

function getEventNumberSpots(event) {
  const spots = event?.numberSpots;
  if (spots == null || spots === "" || Number(spots) <= 0) {
    return null;
  }
  return Number(spots);
}

const DepartmentUserListItem = memo(({ user, catalog, isHead = false, headRoleName, onRemove, isEditMode = false }) => {
  const membershipType = user.membershipType || "Newbie";
  const isSuspended = !!user.suspension?.isSuspended;
  const isLeft = membershipType === "Unbound";
  const displayName =
    user.firstLastName?.trim() || user.fullName || user.name || "Unknown User";
  const positionLabel = user.position || (isHead ? headRoleName : null);

  return (
    <li
      className={`${isSuspended ? "suspended-user" : ""} ${isLeft ? "left-user" : ""} ${
        user.birthDate && isToday(new Date(user.birthDate)) ? "birthday-highlight" : ""
      }${isEditMode ? " department-volunteer-edit-item" : ""}`}
    >
      {isEditMode && onRemove && (
        <button
          type="button"
          className="department-volunteer-remove-btn"
          onClick={() => onRemove(user._id)}
          aria-label={`Remove ${displayName}`}
        >
          <Icon icon="mdi:close" />
        </button>
      )}
      <Link
        to={`/users/${user._id}`}
        className={`user-list-item${isHead ? " department-head-card" : ""}`}
        style={{
          "--department-color": getDepartmentColor(user.departments?.[0], catalog),
        }}
      >
        <div className="user-item-info">
          <div className="user-item-avatar-wrapper">
            <Avatar
              user={user}
              style={{ border: "2px solid rgba(0, 0, 0, 0.6)" }}
            />
          </div>
          {isHead && <span className="department-head-badge">Head</span>}
          <p className="user-item-name">
            {displayName}
            {user.nickname ? ` "${user.nickname}"` : ""}
          </p>
          {user.birthDate && (
            <p className="user-item-birthday">
              <Icon icon="line-md:cake" className="cake-icon" />
              <span className="birthday-text">
                {new Date(user.birthDate).toLocaleDateString()}
              </span>
            </p>
          )}
          <div className="user-item-status">
            <p className="user-item-position">
              {positionLabel ? `${positionLabel} · ${membershipType}` : membershipType}
            </p>
          </div>
          {Array.isArray(user.departments) && user.departments.length > 0 && (
            <p className="user-item-departments">
              {user.departments.map((department, index) => (
                <span
                  key={index}
                  className="user-item-department"
                  style={{
                    "--department-color": getDepartmentColor(department, catalog),
                  }}
                >
                  <div className="dep-icon">
                    {getDepartmentIcon(department, {}, catalog)}
                  </div>
                  <div className="dep-name">{getDepartmentName(department)}</div>
                </span>
              ))}
            </p>
          )}
        </div>
      </Link>
    </li>
  );
});

const DepartmentHeadListItem = memo(({ headUser, department, catalog }) => {
  const departmentColor = getDepartmentColor(department, catalog);
  const headRoleName = getDepartmentHeadRoleName(department, catalog);

  if (!headUser) {
    return (
      <li>
        <div
          className="user-list-item department-head-card department-head-card-empty"
          style={{ "--department-color": departmentColor }}
        >
          <div className="user-item-info">
            <div className="user-item-avatar-wrapper">
              <div className="department-head-placeholder">
                <Icon icon="mdi:account-off-outline" />
              </div>
            </div>
            <span className="department-head-badge department-head-badge-vacant">Head</span>
            <p className="user-item-name">No head assigned</p>
            <p className="department-head-empty-message">
              {headRoleName
                ? `${headRoleName} is currently vacant.`
                : "This department does not have a head assigned yet."}
            </p>
          </div>
        </div>
      </li>
    );
  }

  return (
    <DepartmentUserListItem
      user={headUser}
      catalog={catalog}
      isHead
      headRoleName={headRoleName}
    />
  );
});

function DepartmentPage() {
  const { departmentId } = useParams();
  const history = useHistory();
  const { user: contextUser } = useAuth();
  const { currentSemester: semester } = useSemester();
  const { departments: catalog, refreshDepartments } = useDepartments();

  const [department, setDepartment] = useState(null);
  const [events, setEvents] = useState([]);
  const eventUserRefs = useMemo(() => collectEventUserRefs(events), [events]);
  const {
    users,
    findUser,
    loading: usersLoading,
    formatUserDisplayName,
    refreshUsers,
    semesterUsersLoaded,
  } = useUsers();
  const offRosterIds = useMemo(
    () => offRosterUserIds(eventUserRefs, users, semesterUsersLoaded),
    [eventUserRefs, users, semesterUsersLoaded],
  );
  const { findUserById: findOffRosterUserById } = useFetchUsersByIds(offRosterIds);
  const [openCalls, setOpenCalls] = useState([]);
  const [roleUsersByName, setRoleUsersByName] = useState({});
  const [rolesLoading, setRolesLoading] = useState(true);
  const [semesters, setSemesters] = useState([]);
  const [volunteersSemester, setVolunteersSemester] = useState(null);
  const [volunteerMembers, setVolunteerMembers] = useState([]);
  const [volunteersSemesterUsers, setVolunteersSemesterUsers] = useState([]);
  const [volunteersLoading, setVolunteersLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("volunteers");
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [alertInfo, setAlertInfo] = useState({ show: false, type: "", message: "" });
  const [alertActionInfo, setAlertActionInfo] = useState({
    show: false,
    title: "",
    type: "",
    message: "",
    btn1Action: () => {},
    btn2Action: () => {},
    btn1Text: "",
    btn2Text: "",
  });
  const [isEditMode, setIsEditMode] = useState(false);
  const [localCoverImage, setLocalCoverImage] = useState("");
  const [uploadingCover, setUploadingCover] = useState(false);
  const [coverError, setCoverError] = useState(null);
  const [editMemberIds, setEditMemberIds] = useState([]);
  const baselineMemberIdsRef = useRef([]);
  const wasEditingVolunteersRef = useRef(false);
  const [volunteerSearchQuery, setVolunteerSearchQuery] = useState("");
  const [gmAttendanceSearch, setGmAttendanceSearch] = useState("");
  const [gmAttendanceSortAsc, setGmAttendanceSortAsc] = useState(true);
  const [hrGmAttendanceScope, setHrGmAttendanceScope] = useState(
    HR_GM_ATTENDANCE_SCOPE.DEPARTMENT,
  );
  const [savingMembers, setSavingMembers] = useState(false);

  const departmentRef = useMemo(() => {
    if (!department) return null;
    return department._id || departmentId;
  }, [department, departmentId]);

  const canEditDepartment = useMemo(
    () => canManageDepartmentPage(contextUser, department, catalog),
    [contextUser, department, catalog],
  );

  const catalogDepartment = useMemo(
    () => catalog.find((entry) => String(entry._id) === String(departmentId)),
    [catalog, departmentId],
  );

  const isHrDepartment = useMemo(() => {
    const departmentForName = department || catalogDepartment;
    return getDepartmentName(departmentForName, catalog) === HR_DEPARTMENT_NAME;
  }, [department, catalogDepartment, catalog]);

  const canViewGmAttendance = useMemo(
    () =>
      canViewDepartmentGmAttendance(
        contextUser,
        department || catalogDepartment,
        catalog,
      ),
    [contextUser, department, catalogDepartment, catalog],
  );

  const showGmAttendanceTab = canViewGmAttendance;

  const departmentTabs = useMemo(
    () => buildDepartmentTabs(showGmAttendanceTab),
    [showGmAttendanceTab],
  );

  const generalMeetings = useMemo(() => {
    if (!canViewGmAttendance) return [];

    return events
      .filter((event) => event.isGeneralMeeting)
      .sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [canViewGmAttendance, events]);

  const isEditingCurrentSemester = useMemo(() => {
    if (!semester?._id || !volunteersSemester?._id) return false;
    return String(volunteersSemester._id) === String(semester._id);
  }, [semester?._id, volunteersSemester?._id]);

  const membersDirty = useMemo(() => {
    if (!isEditMode || !isEditingCurrentSemester) return false;

    const baseline = baselineMemberIdsRef.current;
    if (baseline.length !== editMemberIds.length) return true;

    const currentSet = new Set(editMemberIds.map(String));
    return baseline.some((id) => !currentSet.has(String(id)));
  }, [isEditMode, isEditingCurrentSemester, editMemberIds]);

  const departmentEvents = useMemo(() => {
    if (!departmentRef) return [];
    return events
      .filter((event) =>
        event.departments?.some((dep) =>
          departmentMatchesFilter(dep, departmentRef),
        ),
      )
      .sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [events, departmentRef]);

  const {
    currentPage,
    setCurrentPage,
    totalPages,
    goToNextPage,
    goToPreviousPage,
  } = usePagination(EVENTS_PER_PAGE, departmentEvents.length);

  const paginatedDepartmentEvents = useMemo(() => {
    const startIndex = (currentPage - 1) * EVENTS_PER_PAGE;
    return departmentEvents.slice(startIndex, startIndex + EVENTS_PER_PAGE);
  }, [departmentEvents, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [departmentId, semester?._id, setCurrentPage]);

  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages, setCurrentPage]);

  const toggleEventInfo = useCallback((eventId) => {
    setSelectedEventId((current) => (current === eventId ? null : eventId));
  }, []);

  const formatEventListDate = useCallback((dateString) => {
    if (!dateString) return "";
    return format(new Date(dateString), "EEEE, dd.MM.yyyy @ HH:mm");
  }, []);

  const resolveEventUser = useCallback(
    (id) =>
      findUser(id) ||
      users.find((entry) => String(entry._id) === String(id)) ||
      findOffRosterUserById(id),
    [users, findUser, findOffRosterUserById],
  );

  const getUserDisplayName = useCallback(
    (id) => {
      const user = resolveEventUser(id);
      return user ? formatUserDisplayName(user) : "Unknown User";
    },
    [resolveEventUser, formatUserDisplayName],
  );

  const getUserById = useCallback(
    (id) => resolveEventUser(id),
    [resolveEventUser],
  );

  const handleDeleteEvent = useCallback(
    (eventId) => {
      setAlertActionInfo({
        show: true,
        title: "Do you want to delete this event permanently?",
        type: "error",
        message:
          "This action will delete all the information about this event. Are you sure you want to proceed? This cannot be undone.",
        btn2Action: async () => {
          try {
            await eventService.deleteEvent(eventId);
            setEvents((prevEvents) =>
              prevEvents.filter((event) => String(event._id) !== String(eventId)),
            );
            setSelectedEventId((current) =>
              current === eventId ? null : current,
            );
            setAlertInfo({
              show: true,
              type: "success",
              message: "Event deleted successfully!",
            });
          } catch (deleteError) {
            setAlertInfo({
              show: true,
              type: "error",
              message: `Failed to delete event: ${deleteError.message}`,
            });
          } finally {
            setAlertActionInfo((prevState) => ({
              ...prevState,
              show: false,
            }));
          }
        },
        btn1Action: () => {
          setAlertActionInfo((prevState) => ({
            ...prevState,
            show: false,
          }));
        },
        btn1Text: "Not now",
        btn2Text: "Delete",
      });
    },
    [],
  );

  const eventsByOpenCallId = useMemo(
    () => buildEventsByOpenCallId(openCalls, events),
    [openCalls, events],
  );

  const departmentOpenCalls = useMemo(
    () => filterOpenCallsForDepartment(openCalls, events, departmentRef),
    [openCalls, events, departmentRef],
  );

  const { ongoing: ongoingDepartmentOpenCalls, voting: votingDepartmentOpenCalls } =
    useMemo(
      () => categorizeOpenCalls(departmentOpenCalls, eventsByOpenCallId),
      [departmentOpenCalls, eventsByOpenCallId],
    );

  const buildVolunteerList = useCallback((members, semesterUsers) => {
    if (!members?.length) return [];

    return members
      .map((member) => resolveDepartmentMember(member, semesterUsers))
      .filter((user) => user && user.membershipType !== "Unbound")
      .sort(sortVolunteers);
  }, []);

  const currentSemesterVolunteers = useMemo(
    () => buildVolunteerList(department?.members, users),
    [buildVolunteerList, department?.members, users],
  );

  const volunteerLookupUsers = useMemo(() => {
    const byId = new Map();
    [...volunteersSemesterUsers, ...users].forEach((user) => {
      if (user?._id) {
        byId.set(String(user._id), user);
      }
    });
    return Array.from(byId.values());
  }, [volunteersSemesterUsers, users]);

  const getVolunteerNameById = useCallback(
    (id) => {
      const user = volunteerLookupUsers.find(
        (entry) => String(entry._id) === String(id),
      );
      return user ? formatUserDisplayName(user) : "Unknown User";
    },
    [volunteerLookupUsers, formatUserDisplayName],
  );

  const volunteers = useMemo(
    () => buildVolunteerList(volunteerMembers, volunteerLookupUsers),
    [buildVolunteerList, volunteerMembers, volunteerLookupUsers],
  );

  const departmentHeadUser = useMemo(() => {
    const roleName = getDepartmentHeadRoleName(department, catalog);
    if (!roleName) return null;

    const roleHead = roleUsersByName[roleName]?.[0];
    if (!roleHead?._id || roleHead.membershipType === "Unbound") return null;

    const fromRoster = users.find((user) => String(user._id) === String(roleHead._id));
    if (fromRoster?.membershipType === "Unbound") return null;

    return fromRoster || roleHead;
  }, [department, catalog, roleUsersByName, users]);

  const otherVolunteers = useMemo(() => {
    if (!departmentHeadUser?._id) return volunteers;
    return volunteers.filter(
      (user) => String(user._id) !== String(departmentHeadUser._id),
    );
  }, [volunteers, departmentHeadUser]);

  const gmAttendanceMembers = useMemo(() => {
    if (!canViewGmAttendance) return [];

    if (
      isHrDepartment &&
      hrGmAttendanceScope === HR_GM_ATTENDANCE_SCOPE.ORGANIZATION
    ) {
      return [...users].sort(sortVolunteers);
    }

    return buildDepartmentMemberList(
      currentSemesterVolunteers,
      departmentHeadUser,
    );
  }, [
    canViewGmAttendance,
    isHrDepartment,
    hrGmAttendanceScope,
    users,
    currentSemesterVolunteers,
    departmentHeadUser,
  ]);

  const gmAttendanceRows = useMemo(() => {
    if (!canViewGmAttendance) return [];

    return gmAttendanceMembers.map((member) => ({
      user: member,
      ...computeGeneralMeetingAttendance(member._id, generalMeetings),
    }));
  }, [canViewGmAttendance, gmAttendanceMembers, generalMeetings]);

  const filteredGmAttendance = useMemo(() => {
    if (!canViewGmAttendance) return [];

    const rows = gmAttendanceRows.filter(({ user }) =>
      matchesUserSearch(user, gmAttendanceSearch),
    );

    return rows.sort((a, b) => {
      const avgA = a.average == null ? -1 : Number(a.average);
      const avgB = b.average == null ? -1 : Number(b.average);
      if (avgA !== avgB) {
        return gmAttendanceSortAsc ? avgA - avgB : avgB - avgA;
      }
      return sortVolunteers(a.user, b.user);
    });
  }, [
    canViewGmAttendance,
    gmAttendanceRows,
    gmAttendanceSearch,
    gmAttendanceSortAsc,
  ]);

  const {
    currentPage: gmAttendancePage,
    setCurrentPage: setGmAttendancePage,
    totalPages: gmAttendanceTotalPages,
    goToNextPage: goToNextGmAttendancePage,
    goToPreviousPage: goToPreviousGmAttendancePage,
  } = usePagination(
    GM_ATTENDANCE_PER_PAGE,
    canViewGmAttendance ? filteredGmAttendance.length : 0,
  );

  const paginatedGmAttendance = useMemo(() => {
    if (!canViewGmAttendance) return [];

    const startIndex = (gmAttendancePage - 1) * GM_ATTENDANCE_PER_PAGE;
    return filteredGmAttendance.slice(
      startIndex,
      startIndex + GM_ATTENDANCE_PER_PAGE,
    );
  }, [canViewGmAttendance, filteredGmAttendance, gmAttendancePage]);

  useEffect(() => {
    if (!canViewGmAttendance) return;

    setGmAttendancePage(1);
  }, [
    canViewGmAttendance,
    departmentId,
    semester?._id,
    gmAttendanceSearch,
    gmAttendanceSortAsc,
    hrGmAttendanceScope,
    setGmAttendancePage,
  ]);

  useEffect(() => {
    if (!canViewGmAttendance) return;

    if (gmAttendanceTotalPages > 0 && gmAttendancePage > gmAttendanceTotalPages) {
      setGmAttendancePage(gmAttendanceTotalPages);
    }
  }, [
    canViewGmAttendance,
    gmAttendancePage,
    gmAttendanceTotalPages,
    setGmAttendancePage,
  ]);

  const editVolunteers = useMemo(() => {
    if (!editMemberIds.length) return [];

    const sourceUsers = isEditingCurrentSemester ? users : volunteersSemesterUsers;

    return editMemberIds
      .map((memberId) => {
        const fromSemester = sourceUsers.find((user) => String(user._id) === String(memberId));
        if (fromSemester) return fromSemester;
        return volunteers.find((user) => String(user._id) === String(memberId));
      })
      .filter(Boolean)
      .filter((user) => {
        if (!departmentHeadUser?._id) return true;
        return String(user._id) !== String(departmentHeadUser._id);
      })
      .sort(sortVolunteers);
  }, [
    editMemberIds,
    isEditingCurrentSemester,
    users,
    volunteersSemesterUsers,
    volunteers,
    departmentHeadUser,
  ]);

  const availableVolunteersToAdd = useMemo(() => {
    if (!isEditMode || !isEditingCurrentSemester) return [];

    const memberSet = new Set(editMemberIds.map(String));
    const query = volunteerSearchQuery.trim().toLowerCase();

    return users
      .filter((user) => !memberSet.has(String(user._id)))
      .filter((user) => {
        if (!query) return true;
        const name =
          user.firstLastName?.trim() ||
          user.fullName ||
          user.name ||
          "";
        return name.toLowerCase().includes(query);
      })
      .sort(sortVolunteers);
  }, [
    isEditMode,
    isEditingCurrentSemester,
    editMemberIds,
    users,
    volunteerSearchQuery,
  ]);

  const stats = useMemo(() => {
    const now = new Date();
    const pastCount = departmentEvents.filter(
      (event) => event.date && new Date(event.date) < now,
    ).length;
    const upcomingCount = departmentEvents.length - pastCount;

    let attendanceTotal = 0;
    let attendanceEventCount = 0;
    departmentEvents.forEach((event) => {
      const spots = getEventNumberSpots(event);
      if (spots == null) return;
      const participants = event.erasmusParticipantsCount ?? 0;
      attendanceTotal += (participants / spots) * 100;
      attendanceEventCount += 1;
    });

    return {
      events: departmentEvents.length,
      pastEvents: pastCount,
      upcomingEvents: upcomingCount,
      volunteers: currentSemesterVolunteers.length,
      openCalls: countRecruitingOpenCalls(
        ongoingDepartmentOpenCalls,
        votingDepartmentOpenCalls,
      ),
      avgAttendance:
        attendanceEventCount > 0
          ? Math.round(attendanceTotal / attendanceEventCount)
          : null,
    };
  }, [
    departmentEvents,
    currentSemesterVolunteers.length,
    ongoingDepartmentOpenCalls,
    votingDepartmentOpenCalls,
  ]);

  const fetchRoles = useCallback(async () => {
    setRolesLoading(true);
    try {
      const roles = await roleService.getAllRoles();
      const map = {};
      (roles || []).forEach((role) => {
        if (role?.name) {
          map[role.name] = role.assignedUsers || [];
        }
      });
      setRoleUsersByName(map);
    } catch (fetchError) {
      console.error("Failed to fetch roles for department head:", fetchError);
      setRoleUsersByName({});
    } finally {
      setRolesLoading(false);
    }
  }, []);

  const fetchVolunteersForSemester = useCallback(async (options = {}) => {
    const { silent = false, semesterId: semesterIdOverride } = options;
    const targetSemesterId = semesterIdOverride || volunteersSemester?._id;
    if (!departmentId || !targetSemesterId) return null;

    if (!silent) {
      setVolunteersLoading(true);
    }

    try {
      const [departmentData, usersResponse] = await Promise.all([
        departmentService.getDepartment(departmentId, {
          semester: targetSemesterId,
        }),
        axios.get(`${URL}/users/list`, {
          params: {
            semester: targetSemesterId,
            includeSuspended: true,
          },
        }),
      ]);

      setVolunteerMembers(departmentData.members || []);
      setVolunteersSemesterUsers(usersResponse.data || []);
      setDepartment((prev) =>
        prev ? { ...prev, members: departmentData.members || [] } : prev,
      );
      return departmentData;
    } catch (fetchError) {
      console.error("Failed to load department volunteers:", fetchError);
      return null;
    } finally {
      if (!silent) {
        setVolunteersLoading(false);
      }
    }
  }, [departmentId, volunteersSemester?._id]);

  const fetchDepartmentData = useCallback(async () => {
    if (!departmentId || !semester?._id) return;

    setLoading(true);
    setError(null);

    try {
      const catalogDept = catalogDepartment;
      const includeGeneralMeetings =
        isHrDepartment ||
        canViewDepartmentGmAttendance(contextUser, catalogDept, catalog);

      const [departmentData, eventsData, openCallsData] = await Promise.all([
        departmentService.getDepartment(departmentId, { semester: semester._id }),
        eventService.getEventsList(semester._id, {
          ...(includeGeneralMeetings ? { includeGeneralMeetings: "true" } : {}),
        }),
        axios.get(`${opencallURL}/list`, {
          params: {
            semester: semester._id,
            scope: "active",
            includeEvent: true,
          },
        }),
      ]);

      setDepartment(departmentData);
      setEvents(eventsData || []);
      setOpenCalls(openCallsData.data || []);
    } catch (fetchError) {
      console.error("Failed to load department page:", fetchError);
      setError(fetchError.message || "Failed to load department");
      setDepartment(null);
      setEvents([]);
      setOpenCalls([]);
    } finally {
      setLoading(false);
    }
  }, [departmentId, semester?._id, catalog, catalogDepartment, contextUser, isHrDepartment]);

  const refreshVolunteerViews = useCallback(async () => {
    if (!departmentId || !semester?._id) return;

    try {
      const departmentData = await departmentService.getDepartment(departmentId, {
        semester: semester._id,
      });
      setDepartment(departmentData);
    } catch (refreshError) {
      console.error("Failed to refresh department members:", refreshError);
    }

    await Promise.all([fetchVolunteersForSemester(), refreshUsers()]);
  }, [departmentId, semester?._id, fetchVolunteersForSemester, refreshUsers]);

  useEffect(() => {
    fetchDepartmentData();
  }, [fetchDepartmentData]);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  useEffect(() => {
    semesterService
      .getSemesters()
      .then((data) => setSemesters(data || []))
      .catch((fetchError) => {
        console.error("Failed to fetch semesters:", fetchError);
        setSemesters([]);
      });
  }, []);

  useEffect(() => {
    if (semester?._id) {
      setVolunteersSemester(semester);
    }
  }, [departmentId, semester]);

  useEffect(() => {
    fetchVolunteersForSemester();
  }, [fetchVolunteersForSemester]);

  useEffect(() => {
    setIsEditMode(false);
    baselineMemberIdsRef.current = [];
    wasEditingVolunteersRef.current = false;
    setVolunteerSearchQuery("");
    setGmAttendanceSearch("");
    setGmAttendanceSortAsc(true);
    setHrGmAttendanceScope(HR_GM_ATTENDANCE_SCOPE.DEPARTMENT);
    setCoverError(null);
    setActiveTab("volunteers");
  }, [departmentId]);

  useEffect(() => {
    if (!showGmAttendanceTab && activeTab === "gm-attendance") {
      setActiveTab("volunteers");
    }
  }, [showGmAttendanceTab, activeTab]);

  useEffect(() => {
    if (!canEditDepartment) {
      setIsEditMode(false);
    }
  }, [canEditDepartment]);

  useEffect(() => {
    setLocalCoverImage(department?.coverImage || "");
  }, [department?.coverImage]);

  useEffect(() => {
    const isEditingVolunteers = isEditMode && isEditingCurrentSemester;
    const enteringEditMode =
      isEditingVolunteers && !wasEditingVolunteersRef.current;
    wasEditingVolunteersRef.current = isEditingVolunteers;

    if (!enteringEditMode) return;

    const ids = volunteerMembers.map(getMemberId).filter(Boolean);
    baselineMemberIdsRef.current = ids;
    setEditMemberIds(ids);
  }, [isEditMode, isEditingCurrentSemester, volunteerMembers]);

  useEffect(() => {
    if (!isEditMode || !semester?._id) return;
    if (!volunteersSemester || String(volunteersSemester._id) !== String(semester._id)) {
      setVolunteersSemester(semester);
    }
  }, [isEditMode, semester, volunteersSemester]);

  const saveVolunteerMembers = useCallback(
    async (exitEditMode = false) => {
      if (!departmentRef || !semester?._id) return false;

      setSavingMembers(true);
      try {
        const updated = await departmentService.updateMembers(
          departmentRef,
          semester._id,
          editMemberIds,
        );
        const savedMembers = updated.members || [];

        setVolunteerMembers(savedMembers);
        setDepartment((prev) =>
          prev ? { ...prev, members: savedMembers } : prev,
        );
        baselineMemberIdsRef.current = savedMembers
          .map(getMemberId)
          .filter(Boolean);

        await refreshUsers();

        if (exitEditMode) {
          setVolunteerSearchQuery("");
          setCoverError(null);
          setIsEditMode(false);
        }

        void fetchVolunteersForSemester({
          silent: true,
          semesterId: semester._id,
        });
        void refreshDepartments();

        setAlertInfo({
          show: true,
          type: "success",
          message: "Volunteers updated for the current semester.",
        });
        return true;
      } catch (saveError) {
        setAlertInfo({
          show: true,
          type: "error",
          message: saveError.message || "Failed to update volunteers.",
        });
        return false;
      } finally {
        setSavingMembers(false);
      }
    },
    [
      departmentRef,
      semester?._id,
      editMemberIds,
      fetchVolunteersForSemester,
      refreshUsers,
      refreshDepartments,
    ],
  );

  const handleToggleEditMode = useCallback(() => {
    if (isEditMode && membersDirty) {
      setAlertActionInfo({
        show: true,
        title: "Save volunteer changes?",
        type: "warning",
        message: buildVolunteerChangesMessage(
          baselineMemberIdsRef.current,
          editMemberIds,
          getVolunteerNameById,
        ),
        btn1Action: () => {
          setAlertActionInfo((prevState) => ({ ...prevState, show: false }));
        },
        btn2Action: async () => {
          setAlertActionInfo((prevState) => ({ ...prevState, show: false }));
          await saveVolunteerMembers(true);
        },
        btn1Text: "Cancel",
        btn2Text: "Save",
      });
      return;
    }

    if (isEditMode) {
      setVolunteerSearchQuery("");
      setCoverError(null);
      setIsEditMode(false);
      refreshVolunteerViews();
      return;
    }

    setIsEditMode(true);
  }, [
    isEditMode,
    membersDirty,
    editMemberIds,
    getVolunteerNameById,
    saveVolunteerMembers,
    refreshVolunteerViews,
  ]);

  const handleCoverImageChange = useCallback(async (event) => {
    const file = event.target.files[0];
    if (!file || !departmentRef) return;

    setUploadingCover(true);
    setCoverError(null);

    try {
      const updated = await departmentService.uploadCoverImage(departmentRef, file);
      const nextCover = updated.coverImage || "";
      setLocalCoverImage(nextCover);
      setDepartment((prev) => (prev ? { ...prev, coverImage: nextCover } : prev));
      await refreshDepartments();
      setAlertInfo({
        show: true,
        type: "success",
        message: "Cover image updated.",
      });
    } catch (uploadError) {
      setCoverError(uploadError.message || "Failed to upload cover image.");
    } finally {
      setUploadingCover(false);
      event.target.value = "";
    }
  }, [departmentRef, refreshDepartments]);

  const handleDeleteCoverImage = useCallback(async () => {
    if (!departmentRef || !localCoverImage) return;

    setUploadingCover(true);
    setCoverError(null);

    try {
      await departmentService.deleteCoverImage(departmentRef);
      setLocalCoverImage("");
      setDepartment((prev) => (prev ? { ...prev, coverImage: null } : prev));
      await refreshDepartments();
      setAlertInfo({
        show: true,
        type: "success",
        message: "Cover image removed.",
      });
    } catch (deleteError) {
      setCoverError(deleteError.message || "Failed to remove cover image.");
    } finally {
      setUploadingCover(false);
    }
  }, [departmentRef, localCoverImage, refreshDepartments]);

  const handleAddVolunteer = useCallback((userId) => {
    setEditMemberIds((current) => {
      if (current.some((id) => String(id) === String(userId))) {
        return current;
      }
      return [...current, userId];
    });
    setVolunteerSearchQuery("");
  }, []);

  const handleRemoveVolunteer = useCallback((userId) => {
    setEditMemberIds((current) =>
      current.filter((id) => String(id) !== String(userId)),
    );
  }, []);

  const color = getDepartmentColor(department, catalog);
  const coverImage = localCoverImage;

  const renderEventsTab = () => (
    <EventListSection
      loading={loading}
      events={departmentEvents}
      paginatedEvents={paginatedDepartmentEvents}
      sortedEvents={departmentEvents}
      selectedEventId={selectedEventId}
      toggleEventInfo={toggleEventInfo}
      contextUser={contextUser}
      handleDeleteEvent={handleDeleteEvent}
      formatDate={formatEventListDate}
      getUserDisplayName={getUserDisplayName}
      getUserById={getUserById}
      catalog={catalog}
      currentPage={currentPage}
      totalPages={totalPages}
      goToNextPage={goToNextPage}
      goToPreviousPage={goToPreviousPage}
      emptyMessage="No events for this department this semester."
    />
  );

  const renderVolunteersTab = () => {
    if (loading || !volunteersSemester) {
      return <LoadingSpinner message="Loading volunteers" size="sm" />;
    }

    const semesterOptions =
      semesters.length > 0 ? semesters : [volunteersSemester];
    const showVolunteerEdit =
      isEditMode && canEditDepartment && isEditingCurrentSemester;
    const displayedVolunteers = showVolunteerEdit ? editVolunteers : otherVolunteers;

    return (
      <>
        <div className="department-volunteers-toolbar">
          <label
            className="department-volunteers-semester-label"
            htmlFor="department-volunteers-semester"
          >
            Members in semester
          </label>
          <select
            id="department-volunteers-semester"
            className="department-volunteers-semester-select"
            value={volunteersSemester._id}
            disabled={showVolunteerEdit}
            onChange={(event) => {
              const selected = semesterOptions.find(
                (entry) => String(entry._id) === event.target.value,
              );
              if (selected) {
                setVolunteersSemester(selected);
              }
            }}
          >
            {semesterOptions.map((entry) => (
              <option key={entry._id} value={entry._id}>
                {entry.name}
                {entry.isActive ? " (Current)" : ""}
              </option>
            ))}
          </select>
        </div>

        {showVolunteerEdit && (
          <div className="department-volunteers-add-panel">
            <label className="department-volunteers-add-label" htmlFor="department-volunteer-search">
              Add volunteer
            </label>
            <input
              id="department-volunteer-search"
              type="search"
              className="department-volunteers-add-search"
              placeholder="Search volunteers in the current semester..."
              value={volunteerSearchQuery}
              onChange={(event) => setVolunteerSearchQuery(event.target.value)}
            />
            {availableVolunteersToAdd.length > 0 ? (
              <ul className="department-volunteers-add-list">
                {availableVolunteersToAdd.map((user) => {
                  const displayName =
                    user.firstLastName?.trim() ||
                    user.fullName ||
                    user.name ||
                    "Unknown User";
                  return (
                    <li key={user._id}>
                      <button
                        type="button"
                        className="department-volunteers-add-option"
                        onClick={() => handleAddVolunteer(user._id)}
                      >
                        <Avatar user={user} size={32} />
                        <span>{displayName}</span>
                        <Icon icon="mdi:plus" />
                      </button>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="department-volunteers-add-empty">
                {volunteerSearchQuery.trim()
                  ? "No matching volunteers found."
                  : "All current-semester volunteers are already in this department."}
              </p>
            )}
          </div>
        )}

        {volunteersLoading ||
        ((usersLoading || rolesLoading) &&
          displayedVolunteers.length === 0 &&
          !departmentHeadUser) ? (
          <LoadingSpinner message="Loading volunteers" size="sm" />
        ) : (
          <>
            <ul className="users-list department-volunteers-list">
              <DepartmentHeadListItem
                headUser={departmentHeadUser}
                department={department}
                catalog={catalog}
              />
              {displayedVolunteers.map((user) => (
                <DepartmentUserListItem
                  key={user._id}
                  user={user}
                  catalog={catalog}
                  isEditMode={showVolunteerEdit}
                  onRemove={showVolunteerEdit ? handleRemoveVolunteer : undefined}
                />
              ))}
            </ul>
            {displayedVolunteers.length === 0 && (
              <p className="department-volunteers-empty-note">
                {showVolunteerEdit
                  ? "No other members yet. Search above to add volunteers."
                  : `No other members in ${volunteersSemester.name}.`}
              </p>
            )}
          </>
        )}
      </>
    );
  };

  const renderOpenCallsTab = () => (
    <OpenCallList
      embedded
      departmentFilter={departmentRef}
      initialOpenCalls={openCalls}
      initialEvents={events}
      initialUsers={users}
      skipInitialFetch={!loading}
    />
  );

  const renderGmAttendanceTab = () => {
    if (!canViewGmAttendance) {
      return null;
    }

    if (loading || usersLoading) {
      return <LoadingSpinner message="Loading attendance" size="sm" />;
    }

    const isHrOrganizationScope =
      isHrDepartment &&
      hrGmAttendanceScope === HR_GM_ATTENDANCE_SCOPE.ORGANIZATION;

    const gmAttendanceIntro = isHrOrganizationScope
      ? "Average attendance to general meetings for all organization members this semester"
      : isHrDepartment
        ? `Average attendance to general meetings for ${department?.name || HR_DEPARTMENT_NAME} members this semester`
        : `Average attendance to general meetings for ${department?.name || "this department"} members this semester`;

    return (
      <>
        <p className="department-gm-attendance-intro">
          {gmAttendanceIntro}
          {generalMeetings.length > 0
            ? ` (${generalMeetings.length} meeting${generalMeetings.length === 1 ? "" : "s"})`
            : ""}
          .
        </p>

        <div className="department-gm-attendance-toolbar">
          {isHrDepartment && (
            <button
              type="button"
              className={`department-gm-attendance-scope-card${
                isHrOrganizationScope ? " is-organization" : " is-department"
              }`}
              onClick={() =>
                setHrGmAttendanceScope((current) =>
                  current === HR_GM_ATTENDANCE_SCOPE.DEPARTMENT
                    ? HR_GM_ATTENDANCE_SCOPE.ORGANIZATION
                    : HR_GM_ATTENDANCE_SCOPE.DEPARTMENT,
                )
              }
              aria-pressed={isHrOrganizationScope}
            >
              {isHrOrganizationScope
                ? "Show only HR members"
                : "Show all ESNers"}
            </button>
          )}
          <div className="department-gm-attendance-search search-bar">
            <input
              type="text"
              placeholder="Search by name..."
              value={gmAttendanceSearch}
              onChange={(event) => setGmAttendanceSearch(event.target.value)}
            />
            <i className="uil uil-search search-icon" />
          </div>
          <button
            type="button"
            className={`department-gm-attendance-sort-icon${gmAttendanceSortAsc ? "" : " is-desc"}`}
            onClick={() => setGmAttendanceSortAsc((prev) => !prev)}
            aria-label={
              gmAttendanceSortAsc
                ? "Sort by lowest attendance first"
                : "Sort by highest attendance first"
            }
            title={
              gmAttendanceSortAsc
                ? "Sorted: lowest attendance first"
                : "Sorted: highest attendance first"
            }
          >
            <Icon icon="mdi:sort-numeric-ascending" />
          </button>
        </div>

        {gmAttendanceRows.length === 0 ? (
          <p className="department-gm-attendance-empty">
            {isHrOrganizationScope
              ? "No organization members found for the current semester."
              : "No department members found for the current semester."}
          </p>
        ) : generalMeetings.length === 0 ? (
          <p className="department-gm-attendance-empty">
            No general meetings scheduled this semester yet.
          </p>
        ) : filteredGmAttendance.length === 0 ? (
          <p className="department-gm-attendance-empty">
            No members match your search.
          </p>
        ) : (
          <>
            <ul className="department-gm-attendance-list">
              {paginatedGmAttendance.map(({ user, attended, total, average }) => {
                const displayName =
                  user.firstLastName?.trim() ||
                  user.fullName ||
                  user.name ||
                  "Unknown User";
                const hasRole = userHasAssignedRole(user);
                const roleNames = hasRole
                  ? user.roles.map((role) => role?.name).filter(Boolean).join(", ")
                  : "";
                const isGoodAttendance =
                  average != null && Number(average) >= GM_ATTENDANCE_GOOD_THRESHOLD;

                return (
                  <li
                    key={user._id}
                    className={`department-gm-attendance-row${hasRole ? " has-role" : ""}`}
                  >
                    <Link to={`/users/${user._id}`} className="department-gm-attendance-member">
                      <Avatar user={user} size={36} />
                      <span className="department-gm-attendance-name-block">
                        <span className="department-gm-attendance-name-row">
                          <span className="department-gm-attendance-name">{displayName}</span>
                          {Array.isArray(user.departments) && user.departments.length > 0 && (
                            <span className="user-item-departments department-gm-attendance-departments">
                              {user.departments.map((department, index) => (
                                <span
                                  key={
                                    getDepartmentId(department) ||
                                    getDepartmentName(department, catalog) ||
                                    index
                                  }
                                  className="user-item-department"
                                  style={{
                                    "--department-color": getDepartmentColor(department, catalog),
                                  }}
                                >
                                  <div className="dep-icon">
                                    {getDepartmentIcon(department, {}, catalog)}
                                  </div>
                                  <div className="dep-name">
                                    {getDepartmentName(department, catalog)}
                                  </div>
                                </span>
                              ))}
                            </span>
                          )}
                        </span>
                        {hasRole && (
                          <span className="department-gm-attendance-role-badge">{roleNames}</span>
                        )}
                      </span>
                    </Link>
                    <span className="department-gm-attendance-count">
                      {attended}/{total}
                    </span>
                    <span
                      className={`department-gm-attendance-average${
                        isGoodAttendance ? " is-good" : ""
                      }`}
                    >
                      {average != null ? `${average}%` : "—"}
                    </span>
                  </li>
                );
              })}
            </ul>

            <div className="pagination-controls department-gm-attendance-pagination">
              <button
                type="button"
                className="button"
                onClick={goToPreviousGmAttendancePage}
                disabled={gmAttendancePage === 1}
              >
                <i className="uil uil-angle-left-b" /> Previous
              </button>
              <span className="department-gm-attendance-page-label">
                Page {gmAttendancePage} of {gmAttendanceTotalPages || 1}
              </span>
              <button
                type="button"
                className="button"
                onClick={goToNextGmAttendancePage}
                disabled={
                  gmAttendancePage === gmAttendanceTotalPages ||
                  filteredGmAttendance.length === 0
                }
              >
                Next <i className="uil uil-angle-right-b" />
              </button>
            </div>
          </>
        )}
      </>
    );
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case "volunteers":
        return renderVolunteersTab();
      case "announcements":
        return renderOpenCallsTab();
      case "gm-attendance":
        return renderGmAttendanceTab();
      default:
        return renderEventsTab();
    }
  };

  if (loading && !department) {
    return (
      <div className="page-container">
        <NavHeader />
        <LoadingSpinner message="Loading department" size="sm" />
      </div>
    );
  }

  if (error || !department) {
    return (
      <div className="page-container">
        <NavHeader />
        <div className="department-page-content department-page-error">
          <p>{error || "Department not found."}</p>
          <button
            type="button"
            className="button"
            onClick={() => history.push("/departments-list")}
          >
            Back to departments
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <NavHeader />

      {alertInfo.show && (
        <CustomAlert
          type={alertInfo.type}
          message={alertInfo.message}
          onClose={() => setAlertInfo({ show: false, type: "", message: "" })}
        />
      )}

      {alertActionInfo.show && (
        <CustomAlertWithButtons
          title={alertActionInfo.title}
          type={alertActionInfo.type}
          message={alertActionInfo.message}
          button1Text={alertActionInfo.btn1Text}
          button2Text={alertActionInfo.btn2Text}
          onButton1Click={alertActionInfo.btn1Action}
          onButton2Click={alertActionInfo.btn2Action}
        />
      )}

      <div className="department-page-content">
      <div className="department-page-toolbar">
        <Link to="/departments-list" className="department-back-link">
          <Icon icon="mdi:arrow-left" />
          Departments
        </Link>
        {canEditDepartment && (
          <button
            type="button"
            className={`button department-edit-toggle${isEditMode ? " active" : ""}`}
            onClick={handleToggleEditMode}
            disabled={savingMembers}
          >
            <Icon icon={isEditMode ? "mdi:check" : "mdi:pencil-outline"} />
            {savingMembers ? "Saving..." : isEditMode ? "Done editing" : "Edit"}
          </button>
        )}
      </div>

      <section
        className={`department-hero${isEditMode && canEditDepartment ? " department-hero-editing" : ""}`}
        style={{ "--department-color": color }}
      >
        <div className="department-hero-cover">
          {coverImage ? (
            <img src={coverImage} alt="" className="department-hero-cover-image" />
          ) : (
            <div className="department-hero-cover-fallback">
              {getDepartmentIcon(department, { className: "department-hero-icon" }, catalog)}
            </div>
          )}
          <div className="department-hero-overlay" />
          {isEditMode && canEditDepartment && (
            <div className="department-hero-cover-edit">
              <label className={`department-hero-cover-upload-btn${uploadingCover ? " disabled" : ""}`}>
                <Icon icon="mdi:camera-outline" />
                {uploadingCover ? "Uploading..." : "Change photo"}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleCoverImageChange}
                  disabled={uploadingCover}
                  className="file-upload-input"
                />
              </label>
              {coverImage && (
                <button
                  type="button"
                  className="department-hero-cover-remove-btn"
                  onClick={handleDeleteCoverImage}
                  disabled={uploadingCover}
                >
                  Remove photo
                </button>
              )}
              {coverError && (
                <p className="department-hero-cover-error">{coverError}</p>
              )}
            </div>
          )}
        </div>

        <div className="department-hero-content">
          <div className="department-hero-text">
            <h1 className="department-hero-title">{department.name}</h1>
          </div>

          <div className="department-hero-stats">
            <div className="department-stat">
              <span className="department-stat-value">{stats.events}</span>
              <span className="department-stat-label">Events</span>
              {stats.events > 0 && (
                <span className="department-stat-sub">
                  · {stats.pastEvents} past / {stats.upcomingEvents} upcoming
                </span>
              )}
            </div>
            <div className="department-hero-stats-row">
              <div className="department-stat">
                <span className="department-stat-value">{stats.volunteers}</span>
                <span className="department-stat-label">Volunteers</span>
              </div>
              <div className="department-stat">
                <span className="department-stat-value">{stats.openCalls}</span>
                <span className="department-stat-label">Open Calls</span>
              </div>
              <div className="department-stat">
                <span className="department-stat-value">
                  {stats.avgAttendance != null ? `${stats.avgAttendance}%` : "—"}
                </span>
                <span className="department-stat-label">Avg attendance</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="department-workspace">
        <div className="department-tabs" role="tablist">
          {departmentTabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.id}
              className={`department-tab${activeTab === tab.id ? " active" : ""}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="department-tab-panel" role="tabpanel">
          {renderTabContent()}
        </div>
      </section>
      </div>
    </div>
  );
}

export default DepartmentPage;
